import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ProductIds } from '../constants/productIds';

interface LearningContextType {
  activeProductIds: ProductIds[];
  registerProductIds: (ids: ProductIds[]) => void;
  unregisterProductIds: (ids: ProductIds[]) => void;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export const LearningContext = createContext<LearningContextType | undefined>(undefined);

export const useLearningContext = () => {
  const context = useContext(LearningContext);
  if (!context) {
    throw new Error('useLearningContext must be used within a LearningProvider');
  }
  return context;
};

export const LearningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // We use a state map to keep track of reference counts for each ProductId
  // This allows multiple components to request the same ProductId without
  // one component unmounting causing the ProductId to be removed prematurely.
  const [productReferenceMap, setProductReferenceMap] = useState<Record<string, number>>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const registerProductIds = useCallback((ids: ProductIds[]) => {
    setProductReferenceMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = (next[id] || 0) + 1;
      });
      return next;
    });
  }, []);

  const unregisterProductIds = useCallback((ids: ProductIds[]) => {
    setProductReferenceMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (next[id]) {
          next[id] -= 1;
          if (next[id] <= 0) {
            delete next[id];
          }
        }
      });
      return next;
    });
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const activeProductIds = useMemo(() => {
    return Object.keys(productReferenceMap) as ProductIds[];
  }, [productReferenceMap]);

  const value = useMemo(
    () => ({
      activeProductIds,
      registerProductIds,
      unregisterProductIds,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
    }),
    [activeProductIds, registerProductIds, unregisterProductIds, isDrawerOpen, openDrawer, closeDrawer]
  );

  return <LearningContext.Provider value={value}>{children}</LearningContext.Provider>;
};
